import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { ApiResponse, httpClient } from '@/services/client/http-client'
import { TData } from '@/types/auth'
import {
  AddGiftResponse,
  AddMultipleToCartParams,
  AddToCartParams,
  AddToCartResponse,
  CartMiniResponse,
  CartResponse,
  ChangeCartItemQuantityParams,
  ChangeCartItemQuantityResponse,
  ChangeWishlistItemQuantityParams,
  ChangeWishlistItemQuantityResponse,
  InvalidProductInCartResponse,
  PromotionProduct,
  RemoveGiftItemParams,
  RemoveItemParams,
  UpdateCartItemParams,
  UpdateCartItemResponse,
  UpdateWishlistItemParams,
  UpdateWishlistItemResponse,
} from '@/types/cart'
import {
  AddToWishlistParams,
  RemoveWishlistItemParams,
  WishlistResponse,
} from '@/types/wishlist'
import { CartFormatter } from '../formatter/__f.cart'

import { ToastAddToCartSuccessParams } from '@/components/common/toast/toast-cart'
export const __cCartService = {
  // ===================== CREATE =====================

  addMultipleToCart: async (payload: AddMultipleToCartParams) => {
    const response = await httpClient.V3.post<AddToCartResponse>(
      '/cart/add-multiple',
      payload
    )
    return response
  },

  addToWishlist: async (payload: AddToWishlistParams) => {
    const response = await httpClient.V3.post<AddToCartResponse>(
      '/cart-wishlist/add',
      payload
    )
    return response
  },

  // ===================== READ =====================
  getCart: async ({ isLoggedIn }: { isLoggedIn: boolean }) => {
    // Không truyền device_token vào url sẽ 404, nhưng truyền vào BE cũng không lấy dùng
    const deviceToken = DeviceTokenHelper.get()
    const response = await httpClient.V3.get<CartResponse>(
      `/cart/get/${deviceToken}`,
      {
        params: {
          quantity: 'flash_sale',
          is_login: isLoggedIn ? 1 : 0,
        },
      }
    )

    const formattedResponse = CartFormatter.reFormatCartResponse(response)

    return formattedResponse
  },

  getWishlist: async () => {
    const response = await httpClient.V3.get<WishlistResponse>(
      `/cart-wishlist/${DeviceTokenHelper.get()}`
    )

    if (!Array.isArray(response?.data)) {
      response.data = []
    }
    const formattedResponse = CartFormatter.reFormatWishlistResponse(response)
    return formattedResponse
  },

  // ===================== CART MINI =====================
  getCartMini: async () => {
    const response = await httpClient.V3.get<CartMiniResponse>(
      `/cart/mini/${DeviceTokenHelper.get()}`
    )
    return response
  },

  countCartItem: async () => {
    const response = await httpClient.V4_NEW.get<TData<{ count: number }>>(
      `/cart/count-items-cart/${DeviceTokenHelper.get()}`
    )
    return response.data
  },

  // TODO: nhờ BE fix lại response type
  addToCart: async (
    payload: AddToCartParams,
    toastOptions?: ToastAddToCartSuccessParams
  ) => {
    const response = await httpClient.V1.post<ApiResponse<CartResponse>>(
      '/carts',
      payload
    )

    if (!response.state) {
      throw new Error(
        response?.error || response?.message || 'Lỗi khi thêm vào giỏ hàng'
      )
    }

    return toastOptions
  },

  // ===================== UPDATE =====================

  updateCartItem: async (payload: UpdateCartItemParams) => {
    // array:6 [
    //   "index_id" => "668f7e847ec2a85e6f59ee96-dad2fafb-T1yXo1SSVB"
    //   "variants_option" => array:2 [
    //     "option1" => "Đen"
    //     "option2" => "S"
    //   ]
    //   "device_token" => "174e0ba42dc9777dbaddf3d068e98cba"
    //   "is_edit_variant" => true
    //   "product_id" => "668f7e847ec2a85e6f59ee96"
    //   "old_variant_id" => "dad2fafb"
    // ]
    // "https://apiv2.coolmate.io/api/v1/carts/update"
    const response = await httpClient.V1.post<UpdateCartItemResponse>(
      `/carts/update`,
      payload
    )
    if (response.status === 'failed') {
      throw new Error(response?.message || 'Lỗi khi cập nhật giỏ hàng')
    }
    return response
  },

  updateWishlistItem: async (payload: UpdateWishlistItemParams) => {
    // "https://apiv2.coolmate.io/api/v3/cart-wishlist/update"
    // array:6 [
    //   "variants_option" => array:2 [
    //     "option1" => "Xám nhạt"
    //     "option2" => "2XL"
    //   ]
    //   "device_token" => "174e0ba42dc9777dbaddf3d068e98cba"
    //   "is_edit_variant" => true
    //   "product_id" => "64587ccd63a25764a81ba82d"
    //   "old_variant_id" => "a7b7db2e"
    //   "user_id" => "650c03cb13cfb20eac08095f"
    // ]
    const response = await httpClient.V3.post<UpdateWishlistItemResponse>(
      `/cart-wishlist/update`,
      payload
    )
    return response
  },

  updateWishlistItemQuantity: async (
    payload: ChangeWishlistItemQuantityParams
  ) => {
    // https://apiv2.coolmate.io/api/v3/cart-wishlist/update
    // array:6 [
    //   "box" => false
    //   "product_id" => "64dee0e367a297226f4ee305"
    //   "variant_id" => "02701296"
    //   "quantity" => 2
    //   "device_token" => "174e0ba42dc9777dbaddf3d068e98cba"
    //   "user_id" => ""
    // ]
    const response =
      await httpClient.V3.post<ChangeWishlistItemQuantityResponse>(
        `/cart-wishlist/update`,
        payload
      )
    return response
  },

  // TODO: fix lại
  updateCartItemQuantity: async (payload: ChangeCartItemQuantityParams) => {
    // https://apiv2.coolmate.io/api/v1/carts/update
    // array:6 [
    //   "index_id" => "6662e0267c25cd5d74420b52-0a1aa8a3-SOngwMcnwl"
    //   "box" => false
    //   "product_id" => "6662e0267c25cd5d74420b52"
    //   "variant_id" => "f70af200"
    //   "quantity" => 2
    //   "device_token" => "174e0ba42dc9777dbaddf3d068e98cba"
    // ]
    const response = await httpClient.V1.post<ChangeCartItemQuantityResponse>(
      `/carts/update`,
      payload
    )
    if (response.success === false) {
      throw new Error(response?.message || 'Lỗi khi cập nhật giỏ hàng')
    }
    return response
  },

  // ===================== DELETE =====================
  removeItem: async (payload: RemoveItemParams) => {
    // "https://apiv2.coolmate.io/api/v1/carts/delete"
    // array:5 [
    //   "product_id" => "64587ccd63a25764a81ba82d"
    //   "variants" => "8130851a"
    //   "box" => "0"
    //   "index_id" => "64587ccd63a25764a81ba82d-8130851a-CsgvlxgFTe"
    //   "device_token" => "04f9e96bf6268054f71a8dc2e01f26e8"
    // ]
    const response = await httpClient.V1.post<TData<CartResponse>>(
      `/carts/delete`,
      payload
    )
    return response.data
  },

  removeWishlistItem: async (payload: RemoveWishlistItemParams) => {
    // https://apiv2.coolmate.io/api/v3/cart-wishlist/delete
    // array:4 [
    //   "product_id" => "6662e0267c25cd5d74420b52"
    //   "variant_id" => "ea0e019a"
    //   "device_token" => "04f9e96bf6268054f71a8dc2e01f26e8"
    //   "user_id" => ""
    // ]
    const response = await httpClient.V3.post<TData<CartResponse>>(
      `/cart-wishlist/delete`,
      payload
    )
    return response.data
  },

  removeGiftItem: async (payload: RemoveGiftItemParams) => {
    // https://apiv2.coolmate.io/api/v3/cart/remove-item/{device_token}
    // payload: { "index_id": "669b1eb1d711f4520d0466e6-08bbac29-wDTiU8BYlo" }
    const deviceToken = DeviceTokenHelper.get()
    const response = await httpClient.V3.post<
      ApiResponse<{ index_id: string }>
    >(`/cart/remove-item/${deviceToken}`, payload)
    if (!response.state) {
      throw new Error(response?.message || 'Lỗi khi xóa quà')
    }
    return response
  },

  removeAllCart: async () => {
    const response = await httpClient.V1.post<TData<CartResponse>>(
      '/carts/delete-all',
      {
        device_token: DeviceTokenHelper.get(),
      }
    )
    return response
  },

  // =================== COOLCASH ===================
  applyCoolCash: async (point: number) => {
    const response = await httpClient.V3.post<
      ApiResponse<{
        status: 'success' | 'failed'
        message: string
        data?: unknown
      }>
    >('/coolclub-member/add-point', {
      use_point: point,
      device_token: DeviceTokenHelper.get(),
    })
    if (response.status !== 200 || response.state === false) {
      throw new Error(response?.message)
    }
    return response
  },

  getPromotionProducts: async () => {
    const { data } = await httpClient.V3.get<ApiResponse<PromotionProduct[]>>(
      `/promotion/product-in-cart/${DeviceTokenHelper.get()}`
    )
    return data
  },

  getInvalidProductInCart: async () => {
    try {
      const { data, state } = await httpClient.V4_NEW.get<
        ApiResponse<InvalidProductInCartResponse>
      >(`/cart/get-invalid-cart-items/${DeviceTokenHelper.get()}`)
      if (!state || !data) {
        throw new Error('Lỗi khi lấy sản phẩm không hợp lệ trong giỏ hàng')
      }
      return data
    } catch (error) {
      throw error
    }
  },

  // =================== ADD GIFT ===================
  addGift: async (payload: {
    product_id: string
    quantity: number
    options: {
      id: string
    }
    device_token: string
    index_parent_id: string
    index_id?: string
  }) => {
    const deviceToken = DeviceTokenHelper.get()
    const response = await httpClient.V3.post<AddGiftResponse>(
      `/cart/add-gift/${deviceToken}`,
      {
        product_id: payload.product_id,
        quantity: payload.quantity,
        options: payload.options,
        device_token: payload.device_token,
        index_parent_id: payload.index_parent_id,
        ...(payload.index_id && { index_id: payload.index_id }),
      }
    )

    if (!response.state) {
      throw new Error(response.message || 'Lỗi khi thêm quà')
    }

    return response
  },
}
